import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";
import { DataTable, EmptyState, ErrorState, TrialBanner } from "@/components/DataTable";
import { FilterBar, PageHead, SelectField, TextField } from "@/components/Filters";
import { api, errMsg, inr } from "@/lib/api";
import { addToWatchlist, useFilterOptions, useModuleData } from "./hooks";

export default function Colleges() {
  const opts = useFilterOptions();
  const [f, setF] = useState({ state: "", search: "" });
  const [detail, setDetail] = useState(null);
  const { rows, total, locked, visible, loading, error } = useModuleData("/colleges", f);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  const openDetail = async (name) => {
    try {
      const { data } = await api.get("/colleges/detail", { params: { name } });
      setDetail(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  useEffect(() => {
    const esc = (e) => e.key === "Escape" && setDetail(null);
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, []);

  const columns = [
    {
      key: "name",
      label: "College",
      minWidth: 360,
      render: (r) => (
        <button
          data-testid={`college-open-${r.name}`}
          onClick={() => openDetail(r.name)}
          className="block max-w-[460px] whitespace-normal text-left text-[#1D4ED8] hover:underline"
        >
          {r.name}
        </button>
      ),
    },
    { key: "city", label: "City", width: 140 },
    { key: "state", label: "State", width: 110, render: (r) => <span className="text-slate-600">{r.state || "NA"}</span> },
    { key: "college_type", label: "Type", width: 110 },
    { key: "beds", label: "Beds", align: "right", width: 90, mono: true },
    { key: "established", label: "Estd", width: 80, muted: true },
    {
      key: "branches",
      label: "Branches seen",
      width: 130,
      align: "right",
      highlight: true,
      render: (r) => <span>{r.branches?.length ?? 0}</span>,
    },
  ];

  return (
    <div className="space-y-4" data-testid="colleges-page">
      <PageHead
        title="College Details"
        subtitle="Canonical college records built from published allotment and college-detail batches."
        right={
          <span className="font-mono-num text-sm text-slate-500" data-testid="colleges-count">
            {total.toLocaleString("en-IN")} colleges
          </span>
        }
      />
      <FilterBar onReset={() => setF({ state: "", search: "" })}>
        <SelectField label="State" testId="college-filter-state" value={f.state} onChange={set("state")} options={opts.states} />
        <TextField label="Search" testId="college-filter-search" value={f.search} onChange={set("search")} placeholder="College name" />
      </FilterBar>

      {error && <ErrorState message={error} />}
      {locked && <TrialBanner total={total} visible={visible} />}

      {loading ? (
        <div className="border border-slate-200 bg-white p-10 text-center text-sm text-slate-400">Loading…</div>
      ) : rows.length === 0 ? (
        <EmptyState testId="colleges-empty" title="No colleges found" hint="Colleges appear after an allotment or college-details batch is published." />
      ) : (
        <DataTable
          testId="colleges-table"
          columns={columns}
          rows={rows}
          rowActions={(r) => (
            <button
              data-testid={`college-watch-${r.name}`}
              onClick={() => addToWatchlist(r.name, null)}
              className="text-[11px] font-semibold text-slate-500 hover:text-[#1A365D]"
            >
              Watch
            </button>
          )}
        />
      )}

      {detail && (
        <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/40" onClick={() => setDetail(null)}>
          <div
            data-testid="college-detail-panel"
            onClick={(e) => e.stopPropagation()}
            className="h-full w-full max-w-2xl overflow-auto border-l border-slate-200 bg-white p-6"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">{detail.college.name}</h2>
                <p className="mt-1 text-sm text-slate-500">
                  {[detail.college.city, detail.college.state, detail.college.college_type].filter(Boolean).join(" · ") || "—"}
                </p>
              </div>
              <button data-testid="close-detail-btn" onClick={() => setDetail(null)} className="text-slate-400 hover:text-slate-900">
                <X className="h-5 w-5" />
              </button>
            </div>

            <h3 className="mt-8 text-[11px] font-semibold uppercase tracking-widest text-slate-500">Closing ranks</h3>
            {detail.closing_ranks.length === 0 ? (
              <p className="mt-2 text-sm text-slate-500">No closing rank data published for this college yet.</p>
            ) : (
              <table className="data-table mt-2 border border-slate-200">
                <thead>
                  <tr>
                    <th>Branch</th>
                    <th>Quota</th>
                    <th>Category</th>
                    <th>Round</th>
                    <th className="text-right">Closing</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.closing_ranks.map((c) => (
                    <tr key={`${c.branch}|${c.quota}|${c.category}|${c.state || ""}|${c.year}|${c.round}`}>
                      <td>{c.branch}</td>
                      <td>{c.quota}</td>
                      <td>{c.category || "—"}</td>
                      <td className="col-muted">{c.round}</td>
                      <td className="col-highlight text-right">{Number(c.closing_rank).toLocaleString("en-IN")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <h3 className="mt-8 text-[11px] font-semibold uppercase tracking-widest text-slate-500">Fees</h3>
            {detail.fees.length === 0 ? (
              <p className="mt-2 text-sm text-slate-500">No fee records published for this college yet.</p>
            ) : (
              <table className="data-table mt-2 border border-slate-200">
                <thead>
                  <tr>
                    <th>Branch</th>
                    <th>Quota</th>
                    <th>Round</th>
                    <th>Year</th>
                    <th className="text-right">Total / year</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.fees.map((c) => (
                    <tr key={`${c.branch}|${c.quota}|${c.round || ""}|${c.state || ""}|${c.year}`}>
                      <td>{c.branch}</td>
                      <td>{c.quota || "—"}</td>
                      <td className="col-muted">{c.round || "—"}</td>
                      <td className="col-muted">{c.year}</td>
                      <td className="col-highlight text-right">{inr(c.total_fee)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {detail.locked && (
              <p className="mt-6 border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                Trial view — upgrade to MasterPlan to see every row for this college.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
